import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { v4 as uuidv4 } from 'uuid';
import UserCombobox from '../../../util_components/user_combobox/UserCombobox';
import {
  ALREADY_ASSIGNED,
  TARGET_INELIGIBLE,
  apiCurrentAdvisorName,
  apiErrorCode,
  apiErrorReason,
  assignAdvisor,
  fetchAdvisors
} from './api';
import {
  EMPTY_FILTERS,
  alreadyAssignedNotice,
  assignConfirmText,
  assignNotice,
  assignRequest,
  eligibleTargets,
  listQuery
} from './advisors';
import { Btn, SalesDialog } from '../ui';
import type { AdvisorSummary } from './types';

export type Notify = (type: 'success' | 'error', message: string) => void;

export interface AssignStudent {
  uuid: string;
  name: string;
}

interface Props {
  student: AssignStudent | null;
  notify: Notify;
  onClose: () => void;
  // Called after the server answered (assigned, duplicate or 409) so the host refreshes.
  onDone: () => void;
}

// goserver 400 for a banned / deleted student (reason: banned|deleted).
const STUDENT_INELIGIBLE = 'STUDENT_INELIGIBLE';
const STUDENT_INELIGIBLE_MESSAGE = 'This student is banned/deleted and cannot be assigned';
// Footer inside the body: the confirm step swaps its own buttons in.
const FOOTER =
  'flex flex-wrap items-center justify-end gap-2 border-t border-line-subtle bg-surface-sunken px-4 py-3';

// Mounted only while open, so the request_ref is minted fresh per open and kept across retries.
function AssignBody({
  student,
  notify,
  onClose,
  onDone,
  busyRef
}: Props & { student: AssignStudent; busyRef: MutableRefObject<boolean> }) {
  const [requestRef] = useState(() => uuidv4());
  const [summary, setSummary] = useState<AdvisorSummary[]>([]);
  const [loadError, setLoadError] = useState('');
  const [target, setTarget] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const targets = useMemo(() => eligibleTargets(summary), [summary]);
  const targetName = targets.find((t) => t.uuid === target)?.name || '';
  // Late callbacks must not close or refresh a dialog opened for someone else.
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
      busyRef.current = false;
    },
    [busyRef]
  );

  // limit=1: only the advisor summary is needed, not the rows.
  useEffect(() => {
    let live = true;
    fetchAdvisors(listQuery(EMPTY_FILTERS, 0, 1))
      .then((d) => live && setSummary(d.summary))
      .catch((e: unknown) => live && setLoadError(apiErrorReason(e, 'Failed to load advisors')));
    return () => {
      live = false;
    };
  }, []);

  const onConfirm = () => {
    if (!target || busy) return;
    setBusy(true);
    busyRef.current = true;
    assignAdvisor(assignRequest(student.uuid, target, requestRef))
      .then((res) => {
        notify('success', assignNotice(student.name, res));
        if (!alive.current) return;
        onClose();
        onDone();
      })
      .catch((e: unknown) => {
        const code = apiErrorCode(e);
        if (code === ALREADY_ASSIGNED) {
          notify('error', alreadyAssignedNotice(apiCurrentAdvisorName(e)));
          if (!alive.current) return;
          onClose();
          onDone();
          return;
        }
        const reason = apiErrorReason(e, 'Failed to assign the advisor');
        if (code === STUDENT_INELIGIBLE) notify('error', STUDENT_INELIGIBLE_MESSAGE);
        else
          notify(
            'error',
            code === TARGET_INELIGIBLE ? `Target advisor is not eligible: ${reason}` : reason
          );
        if (alive.current) setConfirming(false);
      })
      .finally(() => {
        if (!alive.current) return;
        busyRef.current = false;
        setBusy(false);
      });
  };

  if (confirming) {
    return (
      <div role="alertdialog" aria-label="Confirm assign advisor">
        <div className="px-4 py-3.5">
          <p className="m-0 text-[13px]">{assignConfirmText(student.name, targetName)}</p>
          <p className="m-0 mt-2 text-xs text-content-muted">
            If this student&apos;s latest trial was recorded as a no-show less than 2h15m ago, the
            assignment will auto-end.
          </p>
        </div>
        <div className={FOOTER}>
          <Btn variant="neutral" onClick={() => setConfirming(false)} disabled={busy}>
            Back
          </Btn>
          <Btn variant="primary" onClick={onConfirm} disabled={busy}>
            {busy ? 'Assigning…' : 'Yes, assign advisor'}
          </Btn>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 py-3.5">
        <p className="m-0 text-xs text-content-muted">{`Pick the advisor for ${student.name}.`}</p>
        <div className="mt-3">
          <UserCombobox
            id="assign-advisor-target"
            label="Advisor"
            placeholder="Assign to…"
            users={targets}
            value={target}
            on_change={setTarget}
            width={280}
          />
        </div>
        {loadError ? (
          <p role="alert" className="m-0 mt-2 text-xs" style={{ color: 'var(--state-error)' }}>
            {loadError}
          </p>
        ) : null}
      </div>
      <div className={FOOTER}>
        <Btn variant="neutral" onClick={onClose}>
          Cancel
        </Btn>
        <Btn variant="primary" onClick={() => setConfirming(true)} disabled={!target}>
          Continue
        </Btn>
      </div>
    </div>
  );
}

// "Assign advisor" for a student with no active advisor (REQ 57); shared by the Not-assigned
// view and the student profile.
function AssignAdvisorModal({ student, notify, onClose, onDone }: Props) {
  const busyRef = useRef(false);
  // Escape, overlay and X all land here: closing mid-request would orphan the response.
  const guardedClose = () => {
    if (!busyRef.current) onClose();
  };
  return (
    <SalesDialog open={!!student} onClose={guardedClose} title="Assign advisor" width={480}>
      {student ? (
        <AssignBody
          key={student.uuid}
          student={student}
          notify={notify}
          onClose={onClose}
          onDone={onDone}
          busyRef={busyRef}
        />
      ) : null}
    </SalesDialog>
  );
}

export default AssignAdvisorModal;
